export class PaymentCalculatorModel {
  constructor(data = {}) {
    this.homeValue                = data.homeValue                || 0;
    this.currentMortgageBalance   = data.currentMortgageBalance   || 0;
    this.estCostsSell             = data.estCostsSell             || 0; 
    this.realtorCommissionsSell   = data.realtorCommissionsSell   || 0;
    this.newHomePurchasePrice     = data.newHomePurchasePrice     || 0;
    this.downPayment              = data.downPayment              || 0;
    this.estClosingCosts          = data.estClosingCosts          || 0; 
    this.realtorCommissionsBuy    = data.realtorCommissionsBuy    || 0; 
    this.loanAmount               = data.loanAmount               || 0;
    this.interestRate             = data.interestRate             || 3.5;
    this.loanTermMonths           = data.loanTermMonths           || 360;
    this.currentHomePayment       = data.currentHomePayment       || 0;
    this.monthlyPaymentDifference = data.monthlyPaymentDifference || 0;
    this.cumulativeMonthlyDiff    = data.cumulativeMonthlyDiff    || 0;
    this.equityOnNewHomePayments  = data.equityOnNewHomePayments  || 0;
    this.annualAppreciation       = data.annualAppreciation       || 3.5;
  }

  get estimatedProfitFromSelling() {
    const rawResult = 
      this.homeValue
      - this.currentMortgageBalance
      - Math.round(this.estCostsSell)
      - Math.round(this.realtorCommissionsSell);
    
    return Math.round(rawResult);
  }

  get estimatedCostsToBuy() {
    const rawResult = 
      Math.round(this.estClosingCosts) + 
      Math.round(this.realtorCommissionsBuy);
    return Math.round(rawResult);
  }

  get estimatedMortgagePayment() {
    const principal = Math.round(this.loanAmount);
    const monthlyInterestRate = this.interestRate / 100 / 12;
    const n = this.loanTermMonths;

    if (principal <= 0 || monthlyInterestRate <= 0 || n <= 0) {
      return 0;
    }

    const pow = Math.pow(1 + monthlyInterestRate, n);
    const numerator = monthlyInterestRate * pow;
    const denominator = pow - 1;
    const rawResult = principal * (numerator / denominator);
    return Math.round(rawResult);
  }

  get calcMonthlyPaymentDifference() {
    const roundedEstimatedPayment = Math.round(this.estimatedMortgagePayment);
    const roundedCurrentPayment = Math.round(this.currentHomePayment);
    return Math.round(roundedEstimatedPayment - roundedCurrentPayment);
  }

  get calcCumulativeMonthlyCostDifference() {
    const diff = this.calcMonthlyPaymentDifference;
    const result = diff * 36;
    return Math.round(result);
  }

  get outstandingPrincipalAfter3Years() {
    const P = Math.round(this.loanAmount);
    const r = this.interestRate / 100 / 12;
    const monthlyPayment = Math.round(this.estimatedMortgagePayment);
    if (P <= 0 || r <= 0 || this.loanTermMonths <= 0 || monthlyPayment <= 0) {
      return 0;
    }

    const x = 36; 
    const powX = Math.pow(1 + r, x);
    const firstPart = P * powX;
    const secondPart = monthlyPayment * ((powX - 1) / r);
    const rawResult = firstPart - secondPart;
    return Math.round(rawResult);
  }

  get calcEquityOnNewHomePayments() {
    const roundedLoanAmount = Math.round(this.loanAmount);
    const roundedOutstandingPrincipal = Math.round(this.outstandingPrincipalAfter3Years);
    return Math.round(roundedLoanAmount - roundedOutstandingPrincipal);
  }

  get appreciatedValueAfter3Years() {
    const purchase = Math.round(this.newHomePurchasePrice);
    const apr = this.annualAppreciation / 100;
    const growthFactor = Math.pow(1 + apr, 3);
    const futureValue = purchase * growthFactor;

    const rawResult = futureValue - purchase;
    return Math.round(rawResult);
  }

  get totalCashDifference() {
    const roundedProfit = Math.round(this.estimatedProfitFromSelling);
    const roundedDownPayment = Math.round(this.downPayment);
    const roundedCostsToBuy = Math.round(this.estimatedCostsToBuy);
    const roundedCumulativeDiff = this.calcCumulativeMonthlyCostDifference;

    const rawResult = 
      roundedProfit -
      roundedDownPayment -
      roundedCostsToBuy -
      roundedCumulativeDiff;

    return Math.round(rawResult);
  }

  get equityInNewHome() {
    const roundedAppreciatedValue = this.appreciatedValueAfter3Years;
    const roundedDownPayment = Math.round(this.downPayment);
    const roundedEquityPayments = this.calcEquityOnNewHomePayments;

    const rawResult = 
      roundedAppreciatedValue +
      roundedDownPayment +
      roundedEquityPayments;

    return Math.round(rawResult);
  }

  get totalCashAndEquity() {
    return this.totalCashDifference + this.equityInNewHome;
  }

  static stripFormatting(value = "") {
    return value.replace(/[^\d.-]/g, "");
  }

  static formatAsCurrency(value = 0) {
    const number = parseFloat(value) || 0;
    return number.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });
  }
}
